/**
 * Mobil PDP commerce dock — tek satın alma yüzeyi.
 * Pickers: pdpDockUpdate(sectionId, payload)
 * Action: pdp-dock:action event
 */
(function () {
  'use strict';

  function pdpDockUpdate(sectionId, payload) {
    document.dispatchEvent(
      new CustomEvent('pdp-dock:update', {
        bubbles: true,
        detail: { sectionId: String(sectionId), ...payload },
      })
    );
  }

  window.pdpDockUpdate = pdpDockUpdate;

  class PdpMobileDock extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.mode = this.dataset.mode || 'form';
      this.defaultAtcLabel = this.dataset.atcLabel || 'Add to cart';
      this.selectLabel = this.dataset.selectLabel || 'Select designs';
      this.unitPriceCents = parseInt(this.dataset.unitPriceCents, 10) || 0;

      this.thumbEl = this.querySelector('.pdp-mobile-dock__thumb');
      this.selectionEl = this.querySelector('[data-dock-selection]');
      this.ctaBtn = this.querySelector('[data-dock-cta]');
      this.ctaLabelEl = this.querySelector('[data-dock-cta-label]');
      this.spinnerEl = this.querySelector('[data-dock-spinner]');
      this.noticeEl = this.querySelector('[data-dock-notice]');
      this.noticeTextEl = this.querySelector('[data-dock-notice-text]');

      this._ctaMode = 'add';
      this._ctaDisabled = this.ctaBtn?.disabled ?? false;
      this._noticeText = '';

      this.qtyEl = this.querySelector('[data-dock-qty]');
      this.qtyValueEl = this.querySelector('[data-dock-qty-value]');

      this._onDockUpdate = this._onDockUpdate.bind(this);
      this._onDockAction = this._onDockAction.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);

      document.addEventListener('pdp-dock:update', this._onDockUpdate);
      document.addEventListener('pdp-dock:action', this._onDockAction);

      this.ctaBtn?.addEventListener('click', () => {
        document.dispatchEvent(
          new CustomEvent('pdp-dock:action', {
            bubbles: true,
            detail: { sectionId: this.sectionId },
          })
        );
      });

      this._initQty();
      this._initReveal();

      if (this.mode === 'form' && typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
      }

      if (this.hasAttribute('data-sold-out')) {
        this._applyCta({ ctaLabel: window.variantStrings?.soldOut || 'Sold out', ctaDisabled: true, ctaMode: 'add' });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pdp-dock:update', this._onDockUpdate);
      document.removeEventListener('pdp-dock:action', this._onDockAction);
      this._variantUnsub?.();
      clearTimeout(this._noticeTimer);
    }

    _root() {
      return document.getElementById(`MainProduct-${this.sectionId}`);
    }

    _onDockUpdate(event) {
      const { sectionId, thumbSrc, selectionLine, ctaLabel, ctaDisabled, ctaMode, loading, notice } =
        event.detail || {};
      if (sectionId !== this.sectionId) return;

      if (notice !== undefined) this._applyNotice(notice);

      if (thumbSrc && this.thumbEl?.tagName === 'IMG') {
        this.thumbEl.src = thumbSrc;
      }

      if (this.selectionEl) {
        if (selectionLine) {
          this.selectionEl.textContent = selectionLine;
          this.selectionEl.hidden = false;
        } else {
          this.selectionEl.textContent = '';
          this.selectionEl.hidden = true;
        }
      }

      if (loading !== undefined) {
        this.spinnerEl?.classList.toggle('hidden', !loading);
        if (this.ctaBtn) this.ctaBtn.disabled = loading || this._ctaDisabled;
      }

      if (ctaLabel !== undefined || ctaDisabled !== undefined || ctaMode !== undefined) {
        this._applyCta({
          ctaLabel: ctaLabel ?? this.ctaLabelEl?.textContent,
          ctaDisabled: ctaDisabled ?? this._ctaDisabled,
          ctaMode: ctaMode ?? this._ctaMode,
        });
      }
    }

    _applyCta({ ctaLabel, ctaDisabled, ctaMode }) {
      this._ctaMode = ctaMode;
      this._ctaDisabled = Boolean(ctaDisabled);
      if (this.ctaLabelEl && ctaLabel) this.ctaLabelEl.textContent = ctaLabel;
      if (this.ctaBtn) {
        this.ctaBtn.disabled = this._ctaDisabled;
        this.ctaBtn.classList.toggle('is-select-mode', ctaMode === 'scroll');
      }
    }

    /* Guidance pill above the bar. Two-phase on purpose: the text is written first and the .is-open
       class is added on the NEXT frame, otherwise the browser collapses the closed and open styles
       into one paint and the entrance never animates. On hide the class comes off immediately (the
       exit transition runs) and the text is only cleared once that transition is over, so it can't
       blank mid-animation. Bails out when the text hasn't changed, so repeated picker updates don't
       restart the animation or re-announce the live region. */
    _applyNotice(text) {
      if (!this.noticeEl || !this.noticeTextEl) return;
      const next = String(text || '').trim();
      if (next === this._noticeText) return;
      this._noticeText = next;
      clearTimeout(this._noticeTimer);

      if (next) {
        this.noticeTextEl.textContent = next;
        requestAnimationFrame(() => {
          if (this._noticeText) this.noticeEl.classList.add('is-open');
        });
      } else {
        this.noticeEl.classList.remove('is-open');
        this._noticeTimer = setTimeout(() => {
          if (!this._noticeText) this.noticeTextEl.textContent = '';
        }, 260);
      }
    }

    _onVariantChange({ data }) {
      if (!data || String(data.sectionId) !== this.sectionId) return;
      const variant = data.variant;
      if (!variant) return;

      const unitEl = this.querySelector('[data-dock-unit-price]');
      if (unitEl && window.Shopify?.formatMoney) {
        unitEl.textContent = window.Shopify.formatMoney(variant.price);
      }

      const compareEl = this.querySelector('[data-dock-compare-price]');
      if (compareEl && window.Shopify?.formatMoney) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (onSale) {
          compareEl.textContent = window.Shopify.formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      if (variant.featured_media?.preview_image?.src && this.thumbEl?.tagName === 'IMG') {
        this.thumbEl.src = variant.featured_media.preview_image.src;
      }

      const soldOut = !variant.available;
      this._applyCta({
        ctaLabel: soldOut ? window.variantStrings?.soldOut || 'Sold out' : this.defaultAtcLabel,
        ctaDisabled: soldOut,
        ctaMode: 'add',
      });
    }

    /* Tetiklemeli görünüm.
       Buy-bar kuralı: çubuk, sayfadaki ATC görüş alanından ÇIKINCA belirir.
       Kalıcı bar üstündeki asıl CTA ile yarışıyor ve aynı fiyatı iki kez
       gösteriyor.

       IntersectionObserver ile kurulmuştu ve ÇALIŞMADI — ne canlı cihazda ne
       de test panelinde tek bir callback üretti (bağımsız bir kontrol
       observer'ı da sıfır callback verdi, yani kurulum değil ortam sorunu).
       Doğrulayamadığım bir mekanizma bırakılmıyor: yerine her yerde çalışan
       ve ölçülebilen dikdörtgen karşılaştırması kondu.

       Maliyet kontrollü: dinleyici pasif, iş rAF içinde ve tek elemanın
       rect'i okunuyor; kare başına bir okuma.

       Güvenli taraf korunuyor: dock varsayılan GÖRÜNÜR, bu kod yalnızca
       `is-tucked` ekler/kaldırır. Hesap hiç çalışmazsa satın alma yolu açık
       kalır.

       Gözlenecek buton şablona göre değişiyor, kural değişmiyor. */
    _initReveal() {
      const root = document.getElementById(`ProductInfo-${this.sectionId}`) || document;

      /* ÇAPA = sayfanın kendi satın alma yüzeyi, ve o yüzey İKİ parçadan oluşuyor.
         Herhangi biri ekrandaysa dock çekilir; ikisi de çıkınca belirir.

         Neden ikisi birden: kural önce yalnız CTA'ya bağlıydı ve tekrarın asıl
         kaynağını ıskalıyordu — şikayet edilen şey buton değil FİYATTI. Form
         ürününde fiyat (y=553) ile ATC (y=615) 62px arayla olduğu için fark
         görünmüyordu; seçicide ATC ya çok aşağıda (y=2102) ya hiç yok, kural
         boşa düşüyor ve ilk ekranda aynı tutar iki kez basılıyordu (18 üründen
         10'u). Fiyat çapası bunu yapısal olarak imkânsız kılıyor.
         Yalnız fiyata bağlamak da yetmez: fiyat ATC'den ~90px önce ekrandan
         çıkıyor, dock o aralıkta sayfadaki butonla birlikte görünür ve bu sefer
         CTA çiftlenirdi. İkisini birden saymak tek doğru kapı.

         Liste sırası önemsiz — hepsi taranıyor, ilk GÖRÜNEN yeter. Seçici
         satırları listede duruyor ama onlara yatırım değil: tasarım-bazlı
         listelemeye geçilip picker kalkınca o seçiciler hiçbir şeye eşleşmeyecek
         ve liste kendiliğinden fiyat + ATC'ye inecek. Silinecek kod bırakmıyor. */
      var seçiciler = [
        '.aycan-buybox .price', // fiyat — her şablonda var, tekrarın kaynağı
        '.product-form__submit', // form ürününün kendi ATC'si
        '.aycan-picker-modal__actions .aycan-design-picker__atc', // satır içi seçicinin ATC'si
        '.aycan-picker-modal__actions .aycan-coaster__atc',
        /* MODALSIZ picker'ın sayfa ATC'si (2026-08-30). Üstteki iki satır yalnız
           modal yolunu kapsıyor; modalsız yolda buton `--pdp` OLMAYAN footer'da
           duruyor ve pdp-mobile-dock.css'teki gizleme daraltıldığı için artık
           mobilde görünür. Çapaya eklenmezse fiyat ekrandan çıktığı anda dock
           belirir ve sayfadaki CTA ile dock'unki AYNI ANDA görünür — çubuğun
           varlık sebebi olan çakışmanın ta kendisi. `:not(--pdp)` şart: modal
           yolunda o footer gizli ve zaten `hasBox` elerdi, ama seçiciyi dar
           tutmak niyeti kodda görünür kılıyor. */
        '.aycan-design-picker__footer:not(.aycan-design-picker__footer--pdp) .aycan-design-picker__atc',
        '.aycan-coaster__footer:not(.aycan-coaster__footer--pdp) .aycan-coaster__atc'
      ];
      var hedefler = [];
      for (var i = 0; i < seçiciler.length; i++) {
        var el = root.querySelector(seçiciler[i]);
        if (el) hedefler.push(el);
      }
      if (!hedefler.length) return;

      this._revealTargets = hedefler;
      this._revealTick = this._revealTick.bind(this);
      this._revealQueued = false;

      this._onRevealScroll = () => {
        if (this._revealQueued) return;
        this._revealQueued = true;
        requestAnimationFrame(this._revealTick);
      };

      window.addEventListener('scroll', this._onRevealScroll, { passive: true });
      window.addEventListener('resize', this._onRevealScroll, { passive: true });

      /* Olaya BAĞIMLI DEĞİL — ve bu bilinçli.
         Hesabın kendisi doğru çalışıyor (elle çağrıldığında ATC ekrandayken
         gizliyor, geçilince gösteriyor); tek sorun hiç çağrılmamasıydı.
         Ne IntersectionObserver ne de `scroll` dinleyicisi tetiklendi —
         dört hedefe birden dinleyici kurup ölçtüm, 0/0/0/0.
         Bir olayın her ortamda doğacağını VARSAYMAK bu kadar temel bir
         davranış için fazla kırılgan: olay gelmezse çubuk ya hep açık ya hep
         kapalı kalıyor ve ikisi de yanlış.
         Bu yüzden ikili kurulum: olay = anında tepki (olduğu yerde),
         düşük frekanslı yoklama = garanti. Kare başına değil 200ms'de bir tek
         rect okuması — scroll dinleyicisinin saniyede 60 kez yaptığı işten
         kat kat ucuz. Sekme arkadayken duruyor. */
      this._revealPoll = setInterval(() => {
        if (document.hidden) return;
        this._revealTick();
      }, 200);

      /* Sekme öne gelince bir kez hesapla.
         Yoklamanın `document.hidden` kapısı pil için doğru ama tek başına yarım:
         sayfa arka planda açıldıysa (yeni sekmede aç — sık kullanılan yol) hiçbir
         tur çalışmıyor ve sekmeye geçildiğinde de kendiliğinden düzelmiyordu;
         düzeltme ilk scroll'a kadar bekliyordu. Kapıyı açan olay burada. */
      this._onRevealVisible = () => {
        if (!document.hidden) this._revealTick();
      };
      document.addEventListener('visibilitychange', this._onRevealVisible);

      /* AÇILIŞTA BİR KEZ HESAPLA.
         Buradaki eski not "açılışta gizleme yok — bilerek" diyordu; gerekçesi,
         scroll olayının hiç gelmediği bir ortamda dock'un kalıcı kaybolmasıydı.
         O gerekçe yukarıdaki 200ms'lik yoklama eklendiğinde geçerliliğini
         yitirdi: yoklama hem gizliyor hem geri getiriyor, yani garanti zaten
         orada — ve olay gelmese bile ilk turda aynı gizlemeyi 200ms sonra
         yapıyordu. Sonuç, korunan bir güvenlik değil, görünür bir çakmaydı:
         dock açılışta beliriyor, sonra kayarak kayboluyor.
         İlk karar bu yüzden ANİMASYONSUZ uygulanıyor — açılış bir geçiş değil,
         başlangıç durumudur. */
      this.classList.add('is-reveal-init');
      this._revealTick();
      const unlock = () => this.classList.remove('is-reveal-init');
      /* rAF arka plandaki sekmede hiç düşmez; zamanlayıcı gecikse de düşer. */
      requestAnimationFrame(() => requestAnimationFrame(unlock));
      this._revealInitTimer = setTimeout(unlock, 300);
    }

    _revealTick() {
      this._revealQueued = false;
      const targets = this._revealTargets;
      if (!targets || !targets.length) return;

      /* Çapalardan HERHANGİ BİRİ ekrandaysa dock çekilir. Hiçbirinin tek pikseli
         görünmüyorsa dock gelir.
         `hasBox` kontrolü şart: gizli ya da 0 boyutlu çapa (varyant tükendi,
         seçici ATC'si henüz basılmadı) "ekranda" sayılmamalı — yoksa dock
         gizlenip sayfada hiç satın alma yüzeyi kalmaz. */
      let visible = false;
      for (let i = 0; i < targets.length; i++) {
        const r = targets[i].getBoundingClientRect();
        const hasBox = r.width > 0 && r.height > 0;
        if (hasBox && r.bottom > 0 && r.top < (window.innerHeight || 0)) {
          visible = true;
          break;
        }
      }
      this.classList.toggle('is-tucked', visible);
    }

    /* Adet kumandası.
       Tek doğruluk kaynağı SAYFADAKİ `input[name="quantity"]`: dock'ta ikinci bir
       form alanı yok (aynı forma bağlı iki `quantity` alanı sepete iki değer
       gönderirdi). Buradaki düğmeler o inputa yazıp `change` yayınlıyor, böylece
       Dawn'ın kendi quantity-input bileşeni ve fiyat/kural mantığı olduğu gibi
       çalışmaya devam ediyor. Değer de oradan geri okunuyor — dock hiçbir zaman
       kendi sayısını tutmuyor. */
    _pageQtyInput() {
      const root = document.getElementById(`ProductInfo-${this.sectionId}`) || document;
      return root.querySelector('input[name="quantity"]');
    }

    _initQty() {
      if (!this.qtyEl) return;
      const input = this._pageQtyInput();
      if (!input) {
        this.qtyEl.remove();
        this.qtyEl = null;
        return;
      }

      this._syncQty = this._syncQty.bind(this);
      this.qtyEl.querySelectorAll('[data-dock-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.dataset.dockQtyStep, 10) || 0;
          const el = this._pageQtyInput();
          if (!el) return;
          const min = parseInt(el.min, 10) || 1;
          const max = parseInt(el.max, 10) || Infinity;
          const stepBy = parseInt(el.step, 10) || 1;
          const next = Math.min(max, Math.max(min, (parseInt(el.value, 10) || min) + step * stepBy));
          if (next === (parseInt(el.value, 10) || min)) return;
          el.value = String(next);
          el.dispatchEvent(new Event('change', { bubbles: true }));
          this._syncQty();
        });
      });

      input.addEventListener('change', this._syncQty);
      this._syncQty();
    }

    _syncQty() {
      const input = this._pageQtyInput();
      if (!input || !this.qtyEl) return;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || Infinity;
      const value = parseInt(input.value, 10) || min;
      if (this.qtyValueEl) this.qtyValueEl.textContent = String(value);
      const minus = this.qtyEl.querySelector('[data-dock-qty-step="-1"]');
      const plus = this.qtyEl.querySelector('[data-dock-qty-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }

    disconnectedCallback() {
      if (this._revealPoll) clearInterval(this._revealPoll);
      if (this._revealInitTimer) clearTimeout(this._revealInitTimer);
      if (this._onRevealScroll) {
        window.removeEventListener('scroll', this._onRevealScroll);
        window.removeEventListener('resize', this._onRevealScroll);
      }
      if (this._onRevealVisible) document.removeEventListener('visibilitychange', this._onRevealVisible);
      document.removeEventListener('pdp-dock:update', this._onDockUpdate);
      document.removeEventListener('pdp-dock:action', this._onDockAction);
    }

    _onDockAction(event) {
      if (event.detail?.sectionId !== this.sectionId) return;

      const root = this._root();
      if (!root) return;

      if (this.mode === 'picker') {
        const picker =
          root.querySelector('aycan-design-picker') ||
          root.querySelector('aycan-coaster-picker') ||
          root.querySelector('aycan-keychain-picker');
        if (!picker) return;

        if (this._ctaMode === 'scroll') {
          picker.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const first = picker.querySelector('.aycan-swatch__btn:not([disabled])');
          first?.focus({ preventScroll: true });
          return;
        }

        const atc =
          picker.querySelector('.aycan-design-picker__atc') ||
          picker.querySelector('.aycan-coaster__atc') ||
          picker.querySelector('.aycan-keychain-picker__atc');
        if (atc) {
          atc.click();
          return;
        }

        const submit =
          root.querySelector('.product-form__submit:not([disabled])') ||
          root.querySelector('form.product-form button[type="submit"]:not([disabled])');
        submit?.click();
        return;
      }

      const submit =
        root.querySelector('.product-form__submit:not([disabled])') ||
        root.querySelector('form.product-form button[type="submit"]:not([disabled])');
      submit?.click();
    }
  }

  customElements.define('pdp-mobile-dock', PdpMobileDock);
})();
